/**
 * Deterministic color-per-category palette.
 *
 * Given a category or clase label (any string), returns a stable set of
 * Tailwind classes so the same category always maps to the same color
 * across the app. Uses a small fixed palette (8 slots) and a djb2-ish
 * hash so users perceive an intentional assignment even though it's
 * automatic. Special-cases the historical APTP mappings (C1 yellow,
 * C2 blue, C3 red) to preserve muscle memory.
 */

const PALETTE = [
  { bg: 'bg-yellow-500/15', text: 'text-yellow-300', border: 'border-yellow-500/40', borderL: 'border-l-yellow-500', dot: 'bg-yellow-500' },
  { bg: 'bg-blue-500/15',   text: 'text-blue-300',   border: 'border-blue-500/40',   borderL: 'border-l-blue-500',   dot: 'bg-blue-500' },
  { bg: 'bg-red-500/15',    text: 'text-red-300',    border: 'border-red-500/40',    borderL: 'border-l-red-500',    dot: 'bg-red-500' },
  { bg: 'bg-green-500/15',  text: 'text-green-300',  border: 'border-green-500/40',  borderL: 'border-l-green-500',  dot: 'bg-green-500' },
  { bg: 'bg-purple-500/15', text: 'text-purple-300', border: 'border-purple-500/40', borderL: 'border-l-purple-500', dot: 'bg-purple-500' },
  { bg: 'bg-pink-500/15',   text: 'text-pink-300',   border: 'border-pink-500/40',   borderL: 'border-l-pink-500',   dot: 'bg-pink-500' },
  { bg: 'bg-orange-500/15', text: 'text-orange-300', border: 'border-orange-500/40', borderL: 'border-l-orange-500', dot: 'bg-orange-500' },
  { bg: 'bg-cyan-500/15',   text: 'text-cyan-300',   border: 'border-cyan-500/40',   borderL: 'border-l-cyan-500',   dot: 'bg-cyan-500' },
];

// Preserve historical APTP color assignments so existing users' muscle
// memory doesn't break (C1 yellow, C2 blue, C3 red). Karting family
// (ROTAX and its sub-classes) is pinned to orange — the visual
// convention across most karting cronometrista reports.
const LEGACY_MAP = {
  C1: 0, // yellow
  C2: 1, // blue
  C3: 2, // red
  ROTAX: 6, // orange
  'MASTER MAX': 6,
  'MASTER_MAX': 6,
  'SENIOR MAX': 6,
  'JUNIOR MAX': 6,
  DD2: 6,
  KARTING: 6,
};

function hash(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i += 1) {
    h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

export function getCategoryColor(label) {
  if (!label) return PALETTE[PALETTE.length - 1];
  const key = String(label).trim().toUpperCase();
  if (key in LEGACY_MAP) return PALETTE[LEGACY_MAP[key]];
  const idx = hash(key) % PALETTE.length;
  return PALETTE[idx];
}

export default getCategoryColor;
